// Bills: open bills only (sent and overdue). Remind, or mark paid outside Stripe with proof (one staff member).
// CHANGE-070: real labels, links to the bill and the partner, and a remind result that says what was sent.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api, cents, month, points } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Sources, Table, dayOf, errorText, useCursor, useKit, type Column } from './kit'
import { PartnerLink, useStaffAction, when } from './common'

type Bill = {
  run_id: string; partner_id: string; partner_name?: string; period: string; amount_cents: number; due_at: string | null
  age_days: number; points_held: number; status: 'sent' | 'overdue' | string; pause_suggested: boolean
  paid_cents?: number; returned?: boolean; billing_email_set?: boolean; last_reminded_at?: string | null
}
const KEY = ['staff', 'bills']

/** The words for a bill's state, from what open_bills really returns. */
function state(b: Bill): string {
  if (b.returned) return 'Payment returned'
  if (b.paid_cents && b.paid_cents > 0 && b.paid_cents < b.amount_cents) return `Part paid, ${cents(b.paid_cents)}`
  if (b.status === 'overdue') return 'Overdue'
  if (b.status === 'sent') return b.age_days > 0 ? 'Past due' : 'Not due yet'
  return b.status
}

export function BillsPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ rows: Bill[]; read_at?: string }>('GET', '/api/staff/bills'), refetchInterval: 60_000 })
  const navigate = useNavigate()
  const rows = q.data?.rows ?? []
  const open = (b: Bill) => navigate(`/staff/bill/${b.run_id}`)
  const { cur } = useCursor(rows, (b) => b.run_id, open)
  if (q.isLoading) return <Busy what="Loading bills" />
  if (q.error) return <Page title="Bills"><Err error={q.error} /></Page>
  const columns: Column<Bill>[] = [
    { key: 'partner', head: 'Partner', primary: true, cell: (b) => <PartnerLink id={b.partner_id} name={b.partner_name ?? b.partner_id} /> },
    { key: 'month', head: 'Month', cell: (b) => <Link className="st-link" to={`/staff/bill/${b.run_id}`}>{month(b.period)}</Link> },
    { key: 'amount', head: 'Amount', num: true, cell: (b) => cents(b.amount_cents) },
    { key: 'due', head: 'Due', nowrap: true, cell: (b) => dayOf(b.due_at) },
    { key: 'late', head: 'Days past due', num: true, cell: (b) => <span className={b.age_days >= 30 ? 'st-bad' : b.age_days > 0 ? 'st-warn' : undefined}>{b.age_days}</span> },
    { key: 'held', head: 'Points held', num: true, cell: (b) => points(b.points_held) },
    { key: 'state', head: 'State', cell: (b) => (
      <span data-testid="staff-bill-state">{state(b)}{b.pause_suggested ? <span className="st-bad">. 30 days late: consider pausing</span> : null}</span>
    ) },
    { key: 'act', head: '', label: 'Do', cell: (b) => <BillActions b={b} /> },
  ]
  return (
    <Page title="Bills" testid="staff-bills"
      sub={<Sources><Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} label="Open bills" live limit={180} /></Sources>}>
      <Table rows={rows} columns={columns} rowKey={(b) => b.run_id} empty="No open bills." cur={cur} onOpen={open}
        rowAttrs={(b) => ({ 'data-testid': 'staff-bill-row', 'data-status': b.status })} />
    </Page>
  )
}

function BillActions({ b }: { b: Bill }) {
  const kit = useKit()
  const remind = useStaffAction((id) => `/api/staff/bills/${id}/remind`, [KEY])
  const oob = useStaffAction<{ proof_note: string }>((id) => `/api/staff/bills/${id}/out-of-band`, [KEY])
  const [proof, setProof] = useState('')
  const [said, setSaid] = useState<string | null>(null)
  const noEmail = b.billing_email_set === false
  const doRemind = () => remind.mutate({ id: b.run_id }, {
    onSuccess: (r) => setSaid(r.emailed ? 'Reminder emailed' : r.recorded === false ? 'Already reminded today, nothing new was sent'
      : 'No billing email, nothing was sent'),
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  const doPaid = async () => {
    const ok = await kit.confirm({
      title: `Mark ${cents(b.amount_cents)} paid`, confirm: `Mark ${cents(b.amount_cents)} paid`,
      body: <>{b.partner_name}, {month(b.period)}. Paid outside Stripe. {points(b.points_held)} held points are released. Proof: {proof.trim()}</>,
    })
    if (ok.ok) oob.mutate({ id: b.run_id, body: { proof_note: proof.trim() } }, { onError: (e) => kit.toast(errorText(e), { bad: true }) })
  }
  return (
    <div className="st-stack st-bill-do">
      <div className="st-row">
        <Button tone="quiet" data-testid="staff-bill-remind" disabled={remind.isPending} onClick={doRemind}
          title={noEmail ? 'This partner has no billing email' : undefined}>Remind</Button>
        <span className="st-faint" data-testid="staff-bill-remind-result">
          {said ?? (noEmail ? 'No billing email' : b.last_reminded_at ? `Last reminded ${when(b.last_reminded_at)}` : '')}
        </span>
      </div>
      <div className="st-row">
        <input className="st-input" placeholder="Proof of payment" value={proof} onChange={(e) => setProof(e.target.value)} data-testid="staff-bill-proof" />
        <Button disabled={proof.trim().length < 3 || oob.isPending} data-testid="staff-bill-oob" onClick={() => void doPaid()}>Mark paid</Button>
      </div>
    </div>
  )
}
